import { useState, useEffect, useCallback, useMemo } from 'react';
import { FiSearch, FiDownload, FiEye, FiEdit, FiTrash2, FiMail, FiPhone, FiCalendar, FiCheckCircle, FiXCircle, FiBookOpen, FiX, FiSave } from 'react-icons/fi';
import { studentService } from '../services/apiService';
import { Country, State } from 'country-state-city';
import * as XLSX from 'xlsx';

const ALL_COUNTRIES = Country.getAllCountries().map((c) => ({ isoCode: c.isoCode, name: c.name }));

const StudentsList = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVerified, setFilterVerified] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editStudent, setEditStudent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, total: 1, totalItems: 0 });

  const editCountryIso = useMemo(() => {
    const found = ALL_COUNTRIES.find((c) => c.name === editForm.country);
    return found ? found.isoCode : null;
  }, [editForm.country]);

  const editStates = useMemo(() => {
    if (!editCountryIso) return [];
    return State.getStatesOfCountry(editCountryIso);
  }, [editCountryIso]);

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: pagination.current, limit: 20, sortBy: 'createdAt', sortOrder: 'desc' };
      if (filterVerified === 'verified') params.isVerified = true;
      else if (filterVerified === 'unverified') params.isVerified = false;

      const response = await studentService.getAllStudents(params);
      if (response?.data) {
        setStudents(response.data);
        if (response.pagination) setPagination({ current: response.pagination.current, total: response.pagination.total, totalItems: response.pagination.totalItems });
      }
    } catch (err) {
      console.error('Error fetching students:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterVerified, pagination.current]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) { fetchStudents(); return; }
    try {
      setLoading(true);
      const response = await studentService.getAllStudents({ search: searchTerm, page: 1, limit: 20 });
      if (response?.data) {
        setStudents(response.data);
        if (response.pagination) setPagination({ current: response.pagination.current, total: response.pagination.total, totalItems: response.pagination.totalItems });
      }
    } catch (err) {
      console.error('Error searching students:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    try {
      const response = await studentService.getAllStudents({ limit: 10000, isVerified: true });
      const all = response.data || [];
      if (!all.length) { alert('No students to export'); return; }
      const exportData = all.map(s => ({
        'Name': s.name || '',
        'Email': s.email || '',
        'Mobile': s.mobileNumber || '',
        'Institute': s.instituteName || '',
        'Course': s.courseName || s.course || '',
        'Year': s.year || s.yearOfStudy || '',
        'Branch': s.branch || '',
        'Roll Number': s.rollNumber || '',
        'City': s.city || '',
        'State': s.state || '',
        'Country': s.country || '',
        'Verified': s.isVerified ? 'Yes' : 'No',
        'Registered On': new Date(s.createdAt).toLocaleDateString(),
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Students');
      XLSX.writeFile(wb, `students_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      alert('Failed to download students list');
    }
  };

  const handlePageChange = (newPage) => setPagination(prev => ({ ...prev, current: newPage }));

  // --- Selection ---
  const toggleSelect = (id) => setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredStudents.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredStudents.map(s => s._id)));
  };

  // --- Delete ---
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this student? This cannot be undone.')) return;
    setIsDeleting(true);
    try {
      await studentService.deleteStudent(id);
      setSelectedIds(prev => { const s = new Set(prev); s.delete(id); return s; });
      fetchStudents();
    } catch (err) {
      alert('Failed to delete student.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected students? This cannot be undone.`)) return;
    setIsDeleting(true);
    try {
      await studentService.bulkDeleteStudents([...selectedIds]);
      setSelectedIds(new Set());
      fetchStudents();
    } catch (err) {
      alert('Failed to bulk delete students.');
    } finally {
      setIsDeleting(false);
    }
  };

  // --- Edit ---
  const openEdit = (student) => {
    setEditStudent(student);
    setEditForm({
      name: student.name || '',
      email: student.email || '',
      mobileNumber: student.mobileNumber || '',
      city: student.city || '',
      instituteName: student.instituteName || '',
      state: student.state || '',
      country: student.country || '',
      zipCode: student.zipCode || '',
      yearOfStudy: student.yearOfStudy || student.year || '',
      course: student.course || student.courseName || '',
      bio: student.bio || '',
      isVerified: student.isVerified || false,
      isActive: student.isActive !== false,
      adminNotes: student.adminNotes || '',
    });
    setSaveError('');
  };

  const handleEditSave = async () => {
    setIsSaving(true);
    setSaveError('');
    try {
      await studentService.updateStudent(editStudent._id, editForm);
      setEditStudent(null);
      fetchStudents();
    } catch (err) {
      setSaveError(err.message || 'Failed to save changes.');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredStudents = students.filter(s => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q) ||
      s.instituteName?.toLowerCase().includes(q) || s.rollNumber?.toLowerCase().includes(q) || s.city?.toLowerCase().includes(q);
  });

  const allSelected = filteredStudents.length > 0 && selectedIds.size === filteredStudents.length;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Youth Ambassadors</h1>
        <p className="mt-1 text-sm text-gray-600">Total: {pagination.totalItems} youth ambassadors</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 relative">
            <input type="text" placeholder="Search by name, email, institute, roll number..."
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
            <FiSearch className="absolute left-3 top-3 text-gray-400" />
          </div>
          <select value={filterVerified} onChange={e => setFilterVerified(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#166534] focus:border-transparent">
            <option value="all">All Youth Ambassadors</option>
            <option value="verified">Verified Only</option>
            <option value="unverified">Unverified Only</option>
          </select>
        </div>
        <div className="mt-4 flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-2">
            <button onClick={handleSearch}
              className="px-4 py-2 bg-[#166534] text-white rounded-lg hover:bg-[#0e3d1f] transition-colors flex items-center">
              <FiSearch className="mr-2" /> Search
            </button>
            {selectedIds.size > 0 && (
              <button onClick={handleBulkDelete} disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center disabled:opacity-60">
                <FiTrash2 className="mr-2" /> Delete Selected ({selectedIds.size})
              </button>
            )}
          </div>
          <button onClick={downloadExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center">
            <FiDownload className="mr-2" /> Export to Excel
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#166534]"></div>
            <p className="mt-2 text-gray-600">Loading students...</p>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="text-center py-12"><p className="text-gray-600">No students found</p></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll}
                        className="rounded border-gray-300 text-[#166534] focus:ring-[#166534]" />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Institute</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course Details</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verified</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStudents.map(student => (
                    <tr key={student._id} className={`hover:bg-gray-50 ${selectedIds.has(student._id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-4">
                        <input type="checkbox" checked={selectedIds.has(student._id)} onChange={() => toggleSelect(student._id)}
                          className="rounded border-gray-300 text-[#166534] focus:ring-[#166534]" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{student.name}</div>
                        <div className="text-sm text-gray-500">{student.rollNumber}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{student.email}</div>
                        <div className="text-sm text-gray-500">{student.mobileNumber}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{student.instituteName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{student.courseName || student.course}</div>
                        <div className="text-sm text-gray-500">
                          {(student.year || student.yearOfStudy) && `Year ${student.year || student.yearOfStudy}`}
                          {student.branch && ` • ${student.branch}`}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{student.city}</div>
                        <div className="text-sm text-gray-500">{student.state}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {student.isVerified ? (
                          <span className="flex items-center text-green-600"><FiCheckCircle className="mr-1" /><span className="text-sm">Yes</span></span>
                        ) : (
                          <span className="flex items-center text-yellow-600"><FiXCircle className="mr-1" /><span className="text-sm">No</span></span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-3">
                          <button onClick={() => setSelectedStudent(student)} className="text-gray-500 hover:text-gray-700" title="View"><FiEye /></button>
                          <button onClick={() => openEdit(student)} className="text-[#166534] hover:text-[#0e3d1f]" title="Edit"><FiEdit /></button>
                          <button onClick={() => handleDelete(student._id)} disabled={isDeleting} className="text-red-500 hover:text-red-700 disabled:opacity-50" title="Delete"><FiTrash2 /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.total > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(pagination.current - 1) * 20 + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(pagination.current * 20, pagination.totalItems)}</span> of{' '}
                    <span className="font-medium">{pagination.totalItems}</span> results
                  </p>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button onClick={() => handlePageChange(pagination.current - 1)} disabled={pagination.current === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">Previous</button>
                    {[...Array(Math.min(pagination.total, 5))].map((_, i) => {
                      const p = i + 1;
                      return (
                        <button key={p} onClick={() => handlePageChange(p)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pagination.current === p ? 'z-10 bg-[#166534] border-[#166534] text-white' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
                          {p}
                        </button>
                      );
                    })}
                    <button onClick={() => handlePageChange(pagination.current + 1)} disabled={pagination.current === pagination.total}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">Next</button>
                  </nav>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* View Modal */}
      {selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-semibold text-gray-900">Student Details</h2>
                <button onClick={() => setSelectedStudent(null)} className="text-gray-400 hover:text-gray-600"><FiX className="h-6 w-6" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Personal Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-sm text-gray-500">Name</p><p className="text-sm font-medium">{selectedStudent.name}</p></div>
                    <div><p className="text-sm text-gray-500">Email</p><p className="text-sm font-medium flex items-center"><FiMail className="mr-1" />{selectedStudent.email}</p></div>
                    <div><p className="text-sm text-gray-500">Mobile</p><p className="text-sm font-medium flex items-center"><FiPhone className="mr-1" />{selectedStudent.mobileNumber}</p></div>
                    <div><p className="text-sm text-gray-500">Date of Birth</p><p className="text-sm font-medium">{selectedStudent.dateOfBirth ? new Date(selectedStudent.dateOfBirth).toLocaleDateString() : 'Not provided'}</p></div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Academic Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-sm text-gray-500">Institute</p><p className="text-sm font-medium flex items-center"><FiBookOpen className="mr-1" />{selectedStudent.instituteName}</p></div>
                    <div><p className="text-sm text-gray-500">Course</p><p className="text-sm font-medium">{selectedStudent.courseName || selectedStudent.course || 'Not provided'}</p></div>
                    <div><p className="text-sm text-gray-500">Year</p><p className="text-sm font-medium">{selectedStudent.year || selectedStudent.yearOfStudy || 'Not provided'}</p></div>
                    <div><p className="text-sm text-gray-500">Branch</p><p className="text-sm font-medium">{selectedStudent.branch || 'Not provided'}</p></div>
                    <div><p className="text-sm text-gray-500">Roll Number</p><p className="text-sm font-medium">{selectedStudent.rollNumber || 'Not provided'}</p></div>
                    <div><p className="text-sm text-gray-500">University</p><p className="text-sm font-medium">{selectedStudent.university || 'Not provided'}</p></div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Location</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-sm text-gray-500">City</p><p className="text-sm font-medium">{selectedStudent.city}</p></div>
                    <div><p className="text-sm text-gray-500">State</p><p className="text-sm font-medium">{selectedStudent.state}</p></div>
                    <div><p className="text-sm text-gray-500">Country</p><p className="text-sm font-medium">{selectedStudent.country}</p></div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Additional</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-sm text-gray-500">Verified</p><p className="text-sm font-medium">{selectedStudent.isVerified ? 'Yes' : 'No'}</p></div>
                    <div><p className="text-sm text-gray-500">Active</p><p className="text-sm font-medium">{selectedStudent.isActive !== false ? 'Yes' : 'No'}</p></div>
                    <div><p className="text-sm text-gray-500">Registered On</p><p className="text-sm font-medium flex items-center"><FiCalendar className="mr-1" />{new Date(selectedStudent.createdAt).toLocaleDateString()}</p></div>
                  </div>
                  {selectedStudent.adminNotes && <div className="mt-2"><p className="text-sm text-gray-500">Admin Notes</p><p className="text-sm">{selectedStudent.adminNotes}</p></div>}
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => { setSelectedStudent(null); openEdit(selectedStudent); }}
                  className="px-4 py-2 bg-[#166534] text-white rounded-lg hover:bg-[#0e3d1f] transition-colors flex items-center">
                  <FiEdit className="mr-2" /> Edit
                </button>
                <button onClick={() => setSelectedStudent(null)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Edit Student</h2>
                <button onClick={() => setEditStudent(null)} className="text-gray-400 hover:text-gray-600"><FiX className="h-6 w-6" /></button>
              </div>
              {saveError && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{saveError}</div>}

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Personal Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Full Name</label>
                      <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                      <input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Mobile</label>
                      <input value={editForm.mobileNumber} onChange={e => setEditForm(f => ({ ...f, mobileNumber: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">City</label>
                      <input value={editForm.city} onChange={e => setEditForm(f => ({ ...f, city: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Country</label>
                      <select
                        value={editForm.country || ''}
                        onChange={e => setEditForm(f => ({ ...f, country: e.target.value, state: '' }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent bg-white"
                      >
                        <option value="">Select a country</option>
                        {ALL_COUNTRIES.map((c) => (
                          <option key={c.isoCode} value={c.name}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">State / Province</label>
                      {editStates.length > 0 ? (
                        <select
                          value={editForm.state || ''}
                          onChange={e => setEditForm(f => ({ ...f, state: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent bg-white"
                        >
                          <option value="">Select a state</option>
                          {editStates.map((s) => (
                            <option key={s.isoCode} value={s.name}>{s.name}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          value={editForm.state || ''}
                          onChange={e => setEditForm(f => ({ ...f, state: e.target.value }))}
                          placeholder={editForm.country ? 'Enter state / province' : 'Select a country first'}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent"
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Academic</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Institute Name</label>
                      <input value={editForm.instituteName} onChange={e => setEditForm(f => ({ ...f, instituteName: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Course</label>
                      <input value={editForm.course} onChange={e => setEditForm(f => ({ ...f, course: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Year of Study</label>
                      <input value={editForm.yearOfStudy} onChange={e => setEditForm(f => ({ ...f, yearOfStudy: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Bio</label>
                      <textarea rows={2} value={editForm.bio} onChange={e => setEditForm(f => ({ ...f, bio: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Admin</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="stu-verified" checked={editForm.isVerified} onChange={e => setEditForm(f => ({ ...f, isVerified: e.target.checked }))}
                        className="rounded border-gray-300 text-[#166534] focus:ring-[#166534]" />
                      <label htmlFor="stu-verified" className="text-sm text-gray-700">Mark as Verified</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="stu-active" checked={editForm.isActive} onChange={e => setEditForm(f => ({ ...f, isActive: e.target.checked }))}
                        className="rounded border-gray-300 text-[#166534] focus:ring-[#166534]" />
                      <label htmlFor="stu-active" className="text-sm text-gray-700">Mark as Active</label>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Admin Notes</label>
                      <textarea rows={2} value={editForm.adminNotes} onChange={e => setEditForm(f => ({ ...f, adminNotes: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#166534] focus:border-transparent" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setEditStudent(null)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors">Cancel</button>
                <button onClick={handleEditSave} disabled={isSaving}
                  className="px-4 py-2 bg-[#166534] text-white rounded-lg hover:bg-[#0e3d1f] transition-colors flex items-center disabled:opacity-60">
                  <FiSave className="mr-2" /> {isSaving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsList;
